// Where a visitor came from: UTM tags, the referring site and the landing page.
// first = the first visit we saw (kept in this browser until sign-up),
// last = the most recent visit that had tags or an outside referrer.
// First-party only and sent to our own server at sign-up. Not kept if the
// visitor declined analytics in the cookie banner.

import { consentChoice } from "./consent";

const FIRST_KEY = "hugo_first_touch";
const LAST_KEY = "hugo_last_touch";
const UTM = ["source", "medium", "campaign", "content", "term"];

const clip = (v, n = 120) => (typeof v === "string" && v ? v.slice(0, n) : undefined);

function externalReferrer(ref, host) {
  if (!ref) return undefined;
  try {
    const url = new URL(ref);
    if (url.hostname === host) return undefined;
    return url.hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

// The touch for the current page load, or null when there's nothing to record.
export function readTouch(loc = window.location, ref = document.referrer, now = new Date()) {
  const params = new URLSearchParams(loc.search || "");
  const touch = {};
  for (const k of UTM) {
    const v = clip(params.get(`utm_${k}`));
    if (v) touch[k] = v.toLowerCase();
  }
  const referrer = externalReferrer(ref, loc.hostname);
  if (referrer) touch.referrer = referrer;
  if (!touch.source && params.get("gclid")) touch.source = "google_ads";
  if (!touch.source && params.get("fbclid")) touch.source = "meta";
  if (!touch.source && params.get("ttclid")) touch.source = "tiktok";
  if (!Object.keys(touch).length) return null;
  touch.landing = clip(loc.pathname, 200);
  touch.at = now.toISOString();
  return touch;
}

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

// Call once per page load. A direct visit with no tags still counts as the
// first touch, so "direct" is recorded rather than lost.
export function recordTouch(loc = window.location, ref = document.referrer, now = new Date()) {
  if (consentChoice() === "denied") return;
  const touch = readTouch(loc, ref, now);
  if (!read(FIRST_KEY)) write(FIRST_KEY, touch || { source: "direct", landing: clip(loc.pathname, 200), at: now.toISOString() });
  if (touch) write(LAST_KEY, touch);
}

export function deviceType(ua = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

// What sign-up sends to the server.
export function attributionForSignup() {
  const first = read(FIRST_KEY);
  const last = read(LAST_KEY) || readTouch() || first;
  if (!first && !last) return { device: deviceType() };
  return { first: first || last, last: last || first, device: deviceType() };
}

export function clearAttribution() {
  try {
    localStorage.removeItem(FIRST_KEY);
    localStorage.removeItem(LAST_KEY);
  } catch {
    /* storage unavailable */
  }
}
